/* Feb-2026 · Admin Email Log
 *
 * Searchable, paginated table of every outbound email logged in
 * `email_send_log`. Clicking a row opens a side-sheet with the
 * rendered HTML preview (stored for emails sent after this deploy).
 *
 * Routes:
 *   GET  /api/admin/email-send-log?limit&skip&status&q  → entries
 *   DELETE /api/admin/email-send-log?status=failed       → purge
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Mail, CheckCircle2, XCircle, Search, Trash2,
  RefreshCw, Loader2, Eye, EyeOff, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input }  from '@/components/ui/input';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import axios from 'axios';

const API  = process.env.REACT_APP_BACKEND_URL || '';
const http = axios.create({ baseURL: API, withCredentials: true });
const PAGE = 50;

async function fetchLog({ status, q, skip }) {
  const params = { limit: PAGE, skip: skip || 0 };
  if (status && status !== 'all') params.status = status;
  if (q) params.q = q;
  const r = await http.get('/api/admin/email-send-log', { params });
  return r.data;
}
async function purgeLog() {
  const r = await http.delete('/api/admin/email-send-log', { params: { status: 'failed' } });
  return r.data;
}

function ts(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    + ' ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
}

// ── Status badge ───────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  return status === 'sent'
    ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10.5px] font-medium text-emerald-400">
        <CheckCircle2 className="h-3 w-3" /> sent
      </span>
    : <span className="inline-flex items-center gap-1 rounded-full bg-rose-500/15 px-2 py-0.5 text-[10.5px] font-medium text-rose-400">
        <XCircle className="h-3 w-3" /> failed
      </span>;
}

// ── HTML Preview sheet ─────────────────────────────────────────────────────
function PreviewSheet({ entry, open, onClose }) {
  if (!entry) return null;
  const hasHtml = !!entry.html_preview;
  return (
    <Sheet open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <SheetContent side="right" className="w-full max-w-2xl overflow-y-auto p-0">
        <SheetHeader className="border-b border-border px-5 py-4">
          <SheetTitle className="text-[14px] font-semibold flex items-center gap-2">
            <Mail className="h-4 w-4 text-primary" />
            {entry.subject || '(no subject)'}
          </SheetTitle>
          <SheetDescription asChild>
            <div className="mt-1.5 space-y-0.5 text-[11.5px] text-muted-foreground">
              <div><span className="font-medium text-foreground">To:</span> {entry.recipient_hint || '—'}</div>
              <div><span className="font-medium text-foreground">Sent:</span> {ts(entry.at)}</div>
              <div><span className="font-medium text-foreground">Provider:</span> {entry.provider}</div>
              {entry.message_id && (
                <div><span className="font-medium text-foreground">Message ID:</span> <code className="text-[10px]">{entry.message_id}</code></div>
              )}
              {entry.error && (
                <div className="text-rose-400"><span className="font-medium">Error:</span> {entry.error}</div>
              )}
              <div><StatusBadge status={entry.status} /></div>
            </div>
          </SheetDescription>
        </SheetHeader>

        <div className="p-5">
          {hasHtml ? (
            <>
              <p className="mb-2 text-[11px] text-muted-foreground flex items-center gap-1">
                <Eye className="h-3 w-3" /> Showing first 3,000 characters of email HTML.
              </p>
              <div
                className="rounded-lg border border-border bg-white"
                style={{ minHeight: 300 }}
                data-testid="email-html-preview"
              >
                {/* sandboxed iframe renders HTML safely */}
                <iframe
                  title="Email preview"
                  srcDoc={entry.html_preview}
                  sandbox="allow-same-origin"
                  style={{ width: '100%', minHeight: 420, border: 'none', borderRadius: 8 }}
                />
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center gap-2 py-12 text-center text-muted-foreground">
              <EyeOff className="h-8 w-8 opacity-30" />
              <p className="text-[12px]">No HTML preview for this email.</p>
              <p className="text-[11px] opacity-60">
                Emails sent after the Feb-2026 deploy store a preview automatically.
              </p>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────
export default function AdminEmailLogPage() {
  const [entries,  setEntries]  = useState([]);
  const [total,    setTotal]    = useState(0);
  const [skip,     setSkip]     = useState(0);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [status,   setStatus]   = useState('all');  // all | sent | failed
  const [q,        setQ]        = useState('');
  const [debouncedQ, setDQ]     = useState('');
  const [purging,  setPurging]  = useState(false);
  const [preview,  setPreview]  = useState(null);   // selected entry

  // debounce search input
  useEffect(() => {
    const t = setTimeout(() => { setDQ(q); setSkip(0); }, 350);
    return () => clearTimeout(t);
  }, [q]);

  const load = useCallback(() => {
    setLoading(true); setError(null);
    fetchLog({ status, q: debouncedQ, skip })
      .then(d => { setEntries(d.entries || []); setTotal(d.total || 0); setLoading(false); })
      .catch(e => { setError(e?.response?.data?.detail || e.message); setLoading(false); });
  }, [status, debouncedQ, skip]);

  useEffect(() => { load(); }, [load]);

  const handlePurge = async () => {
    if (!window.confirm('Purge all failed emails from the log? This cannot be undone.')) return;
    setPurging(true);
    try { await purgeLog(); await load(); }
    finally { setPurging(false); }
  };

  const pages = Math.ceil(total / PAGE);
  const page  = Math.floor(skip / PAGE);

  const sentCount   = entries.filter(e => e.status === 'sent').length;
  const failedCount = entries.filter(e => e.status === 'failed').length;

  return (
    <div className="mx-auto max-w-5xl px-4 py-6" data-testid="admin-email-log-page">

      {/* ── Header ── */}
      <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-[18px] font-bold flex items-center gap-2">
            <Mail className="h-5 w-5 text-primary" /> Email Log
          </h1>
          <p className="mt-0.5 text-[12px] text-muted-foreground">
            All outbound emails — click any row to preview.
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="ghost" onClick={load} disabled={loading}
                  className="h-8 gap-1 text-[11.5px]" data-testid="email-log-refresh">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button size="sm" variant="destructive" onClick={handlePurge}
                  disabled={purging || failedCount === 0}
                  className="h-8 gap-1 text-[11.5px]" data-testid="email-log-purge-btn">
            {purging
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <Trash2 className="h-3.5 w-3.5" />}
            Purge failed
          </Button>
        </div>
      </div>

      {/* ── Filters ── */}
      <div className="mb-4 flex items-center gap-3 flex-wrap">
        {/* status tabs */}
        <div className="flex rounded-lg border border-border bg-muted/30 p-0.5 text-[11.5px]">
          {[['all','All'], ['sent','Sent ✅'], ['failed','Failed ❌']].map(([val, label]) => (
            <button key={val}
              onClick={() => { setStatus(val); setSkip(0); }}
              data-testid={`email-filter-${val}`}
              className={[
                'rounded-md px-3 py-1 transition-colors',
                status === val ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
              ].join(' ')}>
              {label}
            </button>
          ))}
        </div>
        {/* subject search */}
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Search subject…"
            value={q}
            onChange={e => setQ(e.target.value)}
            className="h-8 pl-8 text-[12px]"
            data-testid="email-log-search"
          />
        </div>
        <span className="text-[11.5px] text-muted-foreground ml-auto">
          {total.toLocaleString()} total
        </span>
      </div>

      {/* ── Table ── */}
      {error && (
        <div className="rounded-md border border-rose-500/30 bg-rose-500/5 px-4 py-3 text-[12px] text-rose-400 mb-4">
          {error} — <button onClick={load} className="underline">retry</button>
        </div>
      )}

      <div className="rounded-lg border border-border overflow-auto" data-testid="email-log-table">
        <table className="w-full text-[12px]">
          <thead>
            <tr className="border-b border-border bg-muted/30">
              <th className="px-3 py-2 text-left text-[11px] font-medium text-muted-foreground w-36">Sent at</th>
              <th className="px-3 py-2 text-left text-[11px] font-medium text-muted-foreground">Subject</th>
              <th className="px-3 py-2 text-left text-[11px] font-medium text-muted-foreground w-32">Recipient</th>
              <th className="px-3 py-2 text-center text-[11px] font-medium text-muted-foreground w-20">Status</th>
              <th className="px-3 py-2 text-center text-[11px] font-medium text-muted-foreground w-16">Preview</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="py-10 text-center text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin inline-block" />
              </td></tr>
            )}
            {!loading && entries.length === 0 && (
              <tr><td colSpan={5} className="py-10 text-center text-muted-foreground text-[12px]">
                No emails found.
              </td></tr>
            )}
            {!loading && entries.map((e, i) => (
              <tr key={e.id || i}
                  className="border-b border-border/40 hover:bg-muted/20 cursor-pointer transition-colors"
                  onClick={() => setPreview(e)}
                  data-testid="email-log-row">
                <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{ts(e.at)}</td>
                <td className="px-3 py-2 max-w-[280px] truncate font-medium">{e.subject || '—'}</td>
                <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{e.recipient_hint || '—'}</td>
                <td className="px-3 py-2 text-center"><StatusBadge status={e.status} /></td>
                <td className="px-3 py-2 text-center text-muted-foreground">
                  {e.html_preview ? <Eye className="h-3.5 w-3.5 inline text-primary" /> : <EyeOff className="h-3.5 w-3.5 inline opacity-30" />}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Pagination ── */}
      {pages > 1 && (
        <div className="mt-3 flex items-center justify-between text-[11.5px] text-muted-foreground">
          <span>Page {page + 1} of {pages}</span>
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - PAGE))}
                    className="h-7 px-2" data-testid="email-log-prev">
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            <Button size="sm" variant="ghost" disabled={skip + PAGE >= total} onClick={() => setSkip(skip + PAGE)}
                    className="h-7 px-2" data-testid="email-log-next">
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}

      {/* ── Preview sheet ── */}
      <PreviewSheet entry={preview} open={!!preview} onClose={() => setPreview(null)} />
    </div>
  );
}
